import React from "react";
import { clsx } from "clsx";

import {
    type MediaConfig,
    Media,
    MediaFormat,
} from "~components/shared/media/";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import {
    type VariationComponent,
    getIsOpinionPageTheme,
    ZoneColumnCombo,
} from "~types/types";
import isBottomRowOf from "~utils/isBottomRowOf";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContent4Up.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContent4Up.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [
    ZoneColumnCombo.fullWidthContentWell,
];

export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.snapshotChart,
    MediaFormat.embeddedIframe,
    MediaFormat.loopingVideo,
    MediaFormat.videoImage,
    MediaFormat.defaultImage,
];

const MEDIA_CONFIG: MediaConfig = {
    className: styles.media,
    aspectRatio: 3 / 2,
    desktopMaxWidth: 300,
    mobileMaxWidth: 370,
    showCaption: false,
};

export default function LineupContent4Up(props: VariationComponent) {
    const { items, theme } = props;

    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    if (!items?.length) return null;

    return (
        <VariantBaseWithHeader
            source={props}
            className={styles.LineupContent4Up}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <div className={styles.items}>
                {items.map((item, index) => {
                    const itemClasses = clsx([
                        styles.item,
                        {
                            [styles.bottomOfTwo]: isBottomRowOf({
                                itemsPerRow: 2,
                                itemIndex: index,
                                totalItems: items.length,
                            }),
                            [styles.bottomOfFour]: isBottomRowOf({
                                itemsPerRow: 4,
                                itemIndex: index,
                                totalItems: items.length,
                            }),
                        },
                    ]);

                    return (
                        <div className={itemClasses} key={index}>
                            <Media
                                source={item}
                                priority={MEDIA_PRIORITY}
                                config={MEDIA_CONFIG}
                            />
                            <StoryBlock
                                className={styles.storyBlock}
                                isSecondaryStory
                                isBylineEligible={false}
                                isExtraSmallHeadline
                                story={item}
                                withDek={false}
                                storyIndex={index + 1}
                                isAuthorEyebrow={getIsOpinionPageTheme(theme)}
                            />
                        </div>
                    );
                })}
            </div>
        </VariantBaseWithHeader>
    );
}
